import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { lastValueFrom } from 'rxjs';

import { PushService } from 'rev-shared/push/PushService';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { UserContextService } from 'rev-shared/security/UserContext.Service';

import { IBoxVideo, IWebexRecording, IZoomRecording } from './Import.Types';
import { UploadService } from './Upload.Service';

@Injectable()
export class ImportExternalMediaService {
	public readonly http = inject(HttpClient);
	public readonly PushService = inject(PushService);
	public readonly UploadService = inject(UploadService);
	public readonly UserContext = inject(UserContextService);

	public getBoxVideos(teamId: string): Promise<IBoxVideo[]> {
		const url = teamId && teamId.length ?
			`/media/videos/box/user/media-files?teamId=${teamId}` :
			`/media/videos/box/user/media-files`;

		return this.fetchMedia(
			url,
			'GET'
		).then(response => {
			return response.items;
		});
	}

	public importBoxVideo(fileId: string, title: string, description: string, teamId: string): void {
		this.importMedia(
			'media:AddBoxVideoToAccount',
			{
				fileId,
				title,
				description,
				teamId
			},
			title
		);
	}

	public getWebexRecordings(webexSiteSettingsId: string, teamId: string): Promise<IWebexRecording[]> {
		const url = teamId && teamId.length ?
			`/media/videos/webex/${webexSiteSettingsId}/get-recordings?teamId=${teamId}` :
			`/media/videos/webex/${webexSiteSettingsId}/get-recordings`;

		return this.fetchMedia(
			url,
			'POST'
		).then(response => {
			return response.map(recording => (
				{
					...recording,
					durationInMs: Number(recording.durationSeconds) * SecondMs,
				}
			));
		});
	}

	public importWebexRecording(WebexSiteSettingsId: string, recordingId: string, title: string, recordingFileUrl: string, teamId?: string, transcriptionFileUrl?: string): void {
		this.importMedia(
			'media:AddWebexVideoToAccount',
			{
				WebexSiteSettingsId,
				recordingId,
				title,
				teamId,
				recordingFileUrl,
				transcriptionFileUrl
			},
			title
		);
	}

	public getZoomRecordings(): Promise<IZoomRecording[]> {
		const email = this.UserContext.getUser().email;
		const url = `/media/videos/zoom/${email}/get-recordings`;

		return this.fetchMedia(
			url,
			'POST'
		).then(response => {
			return response.map(recording => (
				{
					...recording,
					durationInMs: Number(recording.durationSeconds) * SecondMs,
				}
			));
		});
	}

	public importZoomRecording(recordingId: string, title: string, recordingFileUrl: string, fileType: string, vttFileUrl: string, vttLanguageCode: string): void {
		this.importMedia(
			'media:AddZoomVideoToAccount',
			{
				recordingId,
				title,
				recordingFileUrl,
				fileType,
				vttFileUrl,
				vttLanguageCode
			},
			title
		);
	}

	private fetchMedia(url: string, method: 'GET' | 'POST' = 'POST'): Promise<any> {
		const request$ = method === 'GET'
			? this.http.get<any>(url)
			: this.http.post<any>(url, {}, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });

		return lastValueFrom(request$);
	}

	private importMedia(command: string, payload: any, title: string): Promise<void> {
		const entry = this.UploadService.createImportStatusEntry(title);

		return this.PushService.dispatchCommand(
			command,
			payload,
			[
				'DownloadingVideoFailed',
				'OriginalVideoInstanceStoringFailed',
				'VideoProcessingFailed',
				'DownloadingVideoFinished'
			]
		)
			.then(result => {
				if (result.eventType === 'DownloadingVideoFinished') {
					entry.status.complete = true;
					entry.id = result.message ? result.message.videoId : null;
					entry.uploadDate = result.message ? result.message.when : null;
				} else {
					entry.status.error = true;
				}
			})
			.catch(() => {
				entry.status.error = true;
			});
	}

}
